import React from 'react'
import ThemeSelector from '../components/ThemeSelector'
import ProfilePictureUpload from '../components/ProfilePictureUpload'
import AccountInfo from '../components/AccountInfo'
import GlassPanel from '../components/UI/GlassPanel'
import SettingsSection from '../components/UI/SettingsSection'
import ConfirmationModal from '../components/UI/ConfirmationModal'
import DangerZone from '../components/DangerZone'
import LoadingSpinner from '../components/UI/LoadingSpinner'
import { useSettings } from '../hooks/useSettings'
import { useDeleteAccount } from '../hooks/useDeleteAccount'
import { useModal } from '../hooks/useModal'

const Settings = () => {
    const { loading, isAuthenticated } = useSettings()
    const { deleteAccount, deleteLoading } = useDeleteAccount()
    const { isOpen: showDeleteConfirm, openModal, closeModal } = useModal()

    const handleConfirmDelete = async () => {
        await deleteAccount()
        closeModal()
    }

    if (loading) {
        return (
            <div className='p-8 flex justify-center items-center'>
                <LoadingSpinner text="Loading settings..." />
            </div>
        )
    }

    if (!isAuthenticated) {
        return (
            <div className='p-8 flex justify-center items-center'>
                <div style={{color: 'var(--theme-text-primary)'}}>Please log in to access settings.</div>
            </div>
        )
    }

    return (
        <div className='p-4 sm:p-6 md:p-8 flex justify-center'>
            <div className="w-full max-w-6xl">
                <GlassPanel>
                    <div className="text-center mb-6 sm:mb-8 md:mb-10">
                        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-2 sm:mb-3" style={{color: 'var(--theme-text-primary)'}}>Settings</h1>
                        <p className="text-sm sm:text-base" style={{color: 'var(--theme-text-secondary)'}}>Manage your account settings and preferences.</p>
                    </div>

                    {/* Account Information Section */}
                    <SettingsSection title="Account Information">
                        <div className="max-w-3xl mx-auto">
                            <AccountInfo />
                        </div>
                    </SettingsSection>

                    {/* Profile Settings Section */}
                    <SettingsSection title="Profile">
                        <div className="max-w-3xl mx-auto">
                            <ProfilePictureUpload />
                        </div>
                    </SettingsSection>

                    {/* Theme Settings Section */}
                    <SettingsSection title="Appearance">
                        <div className="max-w-3xl mx-auto">
                            <ThemeSelector />
                        </div>
                    </SettingsSection>

                    {/* Danger Zone Section */}
                    <SettingsSection title="Danger Zone" variant="danger">
                        <div className="max-w-3xl mx-auto">
                            <DangerZone onDeleteAccount={openModal} />
                        </div>
                    </SettingsSection>

                    {/* Delete Confirmation Modal */}
                    <ConfirmationModal
                        isOpen={showDeleteConfirm}
                        title="Confirm Account Deletion"
                        description="Are you absolutely sure you want to delete your account? This will:"
                        confirmText="Delete Forever"
                        cancelText="Cancel"
                        onConfirm={handleConfirmDelete}
                        onCancel={closeModal}
                        loading={deleteLoading}
                        variant="danger"
                    >
                        <ul className="text-white/70 text-sm mb-6 space-y-1">
                            <li>• Permanently delete your profile</li>
                            <li>• Remove all your data</li>
                            <li>• Log you out immediately</li>
                            <li>• This action cannot be undone</li>
                        </ul>
                    </ConfirmationModal>
                </GlassPanel>
            </div>
        </div>
    )
}

export default Settings